import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AppearanceMode } from "@/theme/palette";
import { CacheProvider } from "@emotion/react";
import { createEmotionCache } from "@/theme";

type ThemeContextType = {
  mode: AppearanceMode;
  setMode: (newMode: AppearanceMode) => void;
  toggleTheme: () => void;
  fontSize: number;
  changeFontSize: (delta: number) => void;
  contrast: "normal" | "high";
  toggleContrast: () => void;
  resetAccessibility: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useThemeMode() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within AppThemeProvider");
  }
  return context;
}

const AppThemeProvider = ({
  children,
  emotionContainer,
  themeHost,
}: {
  children: ReactNode;
  emotionContainer?: HTMLElement | ShadowRoot;
  themeHost: HTMLElement;
}) => {
  const [mode, setMode] = useState<AppearanceMode>("light");
  const [fontSize, setFontSize] = useState<number>(16);
  const [contrast, setContrast] = useState<"normal" | "high">("normal");
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const emotionCache = useMemo(
    () => createEmotionCache(emotionContainer ?? document.head),
    [emotionContainer],
  );

  useEffect(() => {
    const savedMode = localStorage.getItem("theme");
    const savedFontSize = parseInt(
      localStorage.getItem("fontSize") || "16",
      10,
    );
    const contrastStored = localStorage.getItem("contrast");
    const savedContrast: "normal" | "high" =
      contrastStored === "high" ? "high" : "normal";
    setMode(
      savedMode === "dark" || savedMode === "light" ? savedMode : "light",
    );
    setFontSize(savedFontSize || 16);
    setContrast(savedContrast);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    themeHost.setAttribute("data-mode", mode);
    themeHost.setAttribute("data-contrast", contrast);
    themeHost.setAttribute("data-scheme", "neutral");

    return () => {
      themeHost.removeAttribute("data-mode");
      themeHost.removeAttribute("data-contrast");
      themeHost.removeAttribute("data-scheme");
      themeHost.style.fontSize = "";
    };
  }, [mode, contrast, isInitialized, themeHost]);

  useEffect(() => {
    if (!isInitialized) return;
    themeHost.style.fontSize = `${fontSize}px`;
  }, [fontSize, isInitialized, themeHost]);

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("theme", newMode);
      return newMode;
    });
  };

  const changeFontSize = (delta: number) => {
    setFontSize((prevSize) => {
      const newSize = Math.min(Math.max(prevSize + delta, 12), 24);
      localStorage.setItem("fontSize", newSize.toString());
      return newSize;
    });
  };

  const toggleContrast = () => {
    setContrast((prevContrast) => {
      const newContrast = prevContrast === "normal" ? "high" : "normal";
      localStorage.setItem("contrast", newContrast);
      return newContrast;
    });
  };

  const resetAccessibility = () => {
    setFontSize(16);
    setContrast("normal");
    localStorage.setItem("fontSize", "16");
    localStorage.setItem("contrast", "normal");
  };

  if (!isInitialized) return null;

  return (
    <ThemeContext.Provider
      value={{
        mode,
        setMode,
        toggleTheme,
        fontSize,
        changeFontSize,
        contrast,
        toggleContrast,
        resetAccessibility,
      }}
    >
      <CacheProvider value={emotionCache}>{children}</CacheProvider>
    </ThemeContext.Provider>
  );
};

export default AppThemeProvider;
